import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { Operator, OperatorRequest } from './types';
import { createOperator, deleteOperator, getOperator, getOperators, updateOperator } from './api';
import { useNavigate, useParams } from 'react-router-dom';
import { queryClient } from 'providers/AllProviders';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';

export function useGetOperators(
  page: number,
  options?: UseQueryOptions<{ data: Operator[]; meta: MetaApiResponse }, Error>
) {
  return useQuery<{ data: Operator[]; meta: MetaApiResponse }>({
    queryKey: ['operators'],
    queryFn: () => getOperators(page),
    ...options
  });
}

export function useGetOperator(options?: UseQueryOptions<Operator, Error>) {
  const { operatorId } = useParams<{ workspaceId: string; operatorId: string }>();

  return useQuery<Operator>({
    queryKey: ['operator', operatorId],
    queryFn: () => getOperator(operatorId!),
    enabled: Boolean(operatorId),
    ...options
  });
}

export function useCreateOrUpdateOperator(hasToNavigate?: boolean) {
  const navigate = useNavigate();
  const { operatorId } = useParams<{ workspaceId: string; operatorId: string }>();

  return useMutation({
    mutationFn: (payload: OperatorRequest) =>
      operatorId ? updateOperator(operatorId!, payload) : createOperator(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['operators'],
        refetchType: 'active'
      });
      if (operatorId) {
        queryClient.invalidateQueries({
          queryKey: ['operator', operatorId],
          refetchType: 'active'
        });
      }
      if (hasToNavigate === undefined) {
        navigate(-1);
      }
    }
  });
}

export function useDeleteOperator() {
  return useMutation({
    mutationFn: (id: string) => deleteOperator(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['operators'],
        refetchType: 'active'
      });
    }
  });
}
